import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Modal, Form, Alert, Badge, Row, Col, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaMapMarkerAlt, FaBuilding, FaPlus, FaMapMarkedAlt, FaUserPlus, FaFileExcel, FaFilePdf, FaTrash } from 'react-icons/fa';
import { branchesAPI, regionsAPI, sitesAPI, personnelAPI } from '../../utils/api';
import notificationService from '../../services/notificationService';
const StructureManagement = () => {
    const [branches, setBranches] = useState([]);
    const [showAddBranchModal, setShowAddBranchModal] = useState(false);
    const [showAddRegionModal, setShowAddRegionModal] = useState(false);
    const [branchFormData, setBranchFormData] = useState({ name: '' });
    const [regionFormData, setRegionFormData] = useState({ name: '', branchId: '' });
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    // Bulk Assign Sites State
    const [showAssignSitesModal, setShowAssignSitesModal] = useState(false);
    const [availableSites, setAvailableSites] = useState([]);
    const [allSites, setAllSites] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);
    const [siteSearchTerm, setSiteSearchTerm] = useState('');
    // Bulk Assign Personnel State
    const [showAssignPersonnelModal, setShowAssignPersonnelModal] = useState(false);
    const [availablePersonnel, setAvailablePersonnel] = useState([]);
    const [targetEntity, setTargetEntity] = useState(null); // { type: 'branch' | 'region', id, name }
    const [selectedPersonnelIds, setSelectedPersonnelIds] = useState([]);
    const [initialRegionPersonnelIds, setInitialRegionPersonnelIds] = useState([]);
    const [personnelSearchTerm, setPersonnelSearchTerm] = useState('');
    // Orphan-primary modal: shown when removing a site from its primary region.
    // The admin picks a replacement primary (or accepts clearing) before we save.
    const [showOrphanModal, setShowOrphanModal] = useState(false);
    const [orphanSites, setOrphanSites] = useState([]); // [{ siteId, siteName, currentPrimaryRegionId, otherRegions: [...] }]
    const [orphanChoices, setOrphanChoices] = useState({}); // { [siteId]: regionId or '' for clear }

    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'branch' | 'region', id, name }
    useEffect(() => {
        fetchBranches();
        fetchAllSites();
    }, []);
    const fetchBranches = async () => {
        try {
            const response = await branchesAPI.getAll({ includeRelations: true });
            console.log('Fetched branches with regions:', response.data);
            setBranches(response.data);
        } catch (err) {
            console.error("Failed to fetch branches:", err);
            setError("Failed to load branches. Please try again.");
        }
    };
    const fetchAllSites = async () => {
        try {
            const response = await sitesAPI.getAll();
            setAllSites(response.data);
        } catch (err) {
            console.error("Failed to fetch sites:", err);
        }
    };
    const handleBranchInputChange = (e) => {
        const { name, value } = e.target;
        setBranchFormData(prev => ({ ...prev, [name]: value }));
    };
    const handleRegionInputChange = (e) => {
        const { name, value } = e.target;
        setRegionFormData(prev => ({ ...prev, [name]: value }));
    };
    const handleAddBranch = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            const response = await branchesAPI.create(branchFormData);
            const branchName = response.data?.name || branchFormData.name;

            // Trigger notification
            notificationService.triggerStructureAction('branch-added', branchName);

            fetchBranches(); // Refresh list to get all data including default fields
            setShowAddBranchModal(false);
            setSuccess('Branch added successfully!');
            setBranchFormData({ name: '' });
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error("Failed to create branch:", err);
            setError(err.response?.data?.message || "Failed to create branch. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };
    const openAddRegionModal = (branch) => {
        setSelectedBranch(branch);
        setRegionFormData({ name: '', branchId: branch.id });
        setShowAddRegionModal(true);
    };
    const handleAddRegion = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            const response = await regionsAPI.create({
                name: regionFormData.name,
                branchId: selectedBranch.id
            });
            const regionName = response.data?.name || regionFormData.name;

            // Trigger notification
            notificationService.triggerStructureAction('region-added', regionName, {
                branchName: selectedBranch.name
            });

            fetchBranches(); // Refresh to show new region
            setShowAddRegionModal(false);
            setSuccess('Region added successfully!');
            setRegionFormData({ name: '', branchId: '' });
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error("Failed to create region:", err);
            setError(err.response?.data?.message || "Failed to create region. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };
    const openAssignSitesModal = async (region) => {
        setSelectedRegion(region);
        setSelectedSiteIds([]);
        setSiteSearchTerm('');
        setShowAssignSitesModal(true);
        setIsLoading(true); // Show loading while fetching sites
        try {
            // Fetch the full site catalogue (for the modal table) AND the AUTHORITATIVE
            // current memberships for this region from the backend. The memberships
            // endpoint OR's primary Site.regionId + SiteRegions, so multi-assigned
            // sites (secondary in this region) are correctly pre-checked. Without this
            // they'd appear unchecked and the next save would remove them.
            const [allSitesResp, assignedResp] = await Promise.all([
                sitesAPI.getAll(),
                regionsAPI.getAssignedSites(region.id),
            ]);
            setAvailableSites(allSitesResp.data);

            // getAssignedSites returns { success, data: [...sites], count } per
            // region.controller. Be tolerant of legacy shapes just in case.
            const assignedPayload = assignedResp?.data;
            const assignedSites = Array.isArray(assignedPayload)
                ? assignedPayload
                : (assignedPayload?.data || assignedPayload?.sites || []);

            // Build the pre-selected set from THREE sources, in order of trust:
            //   1. The authoritative getAssignedSites response (M:N + legacy)
            //   2. region.sites attached by branches API (covers stale refresh races)
            //   3. site.regionId === region.id from the catalogue (legacy fallback)
            const currentMemberIds = new Set();
            assignedSites.forEach(s => currentMemberIds.add(s.id));
            (region.sites || []).forEach(s => currentMemberIds.add(s.id));
            (region.regionSites || []).forEach(s => currentMemberIds.add(s.id));
            allSitesResp.data.forEach(site => {
                if (site.regionId === region.id) currentMemberIds.add(site.id);
            });

            setSelectedSiteIds([...currentMemberIds]);
        } catch (err) {
            console.error("Failed to fetch sites:", err);
            setError("Failed to load sites. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };
    const handleSiteSelectionChange = (siteId) => {
        setSelectedSiteIds(prev => {
            if (prev.includes(siteId)) {
                return prev.filter(id => id !== siteId);
            } else {
                return [...prev, siteId];
            }
        });
    };
    // Submit the assign-sites payload. When `opts` is empty we ask the backend
    // to perform the orphan check (withOrphanCheck: true); if the response is
    // 409 we open the orphan modal so the admin can pick replacement primaries.
    // Subsequent retries pass the admin's picks via opts.replacementPrimaries
    // (and optionally allowClearPrimary).
    const submitAssignSites = async (opts = {}) => {
        if (!selectedRegion) return;
        setIsLoading(true);
        setError('');
        try {
            await regionsAPI.assignSites(selectedRegion.id, selectedSiteIds, {
                withOrphanCheck: true,
                ...opts
            });

            console.log('[StructureManagement] Triggering site-assigned notification for region:', selectedRegion.name, 'with', selectedSiteIds.length, 'sites');
            notificationService.triggerStructureAction('site-assigned', selectedRegion.name, {
                siteCount: selectedSiteIds.length,
                regionName: selectedRegion.name
            });

            setShowAssignSitesModal(false);
            setShowOrphanModal(false);
            setOrphanSites([]);
            setOrphanChoices({});
            const count = selectedSiteIds.length;
            setSuccess(count > 0
                ? `Successfully assigned ${count} site${count !== 1 ? 's' : ''} to ${selectedRegion.name}!`
                : `Successfully cleared all sites from ${selectedRegion.name}!`
            );
            setTimeout(() => setSuccess(''), 3000);
            // Refresh branches/regions data and site list to update site counts accurately
            fetchBranches();
            fetchAllSites();
        } catch (err) {
            // Special handling for the primary-region orphan case.
            const status = err.response?.status;
            const body = err.response?.data;
            if (status === 409 && body && body.code === 'PRIMARY_REGION_ORPHAN' && Array.isArray(body.orphans)) {
                // Initialise admin choices to the first available replacement region per orphan
                // (or '' meaning "clear" when the site has no other memberships).
                const defaults = {};
                body.orphans.forEach(o => {
                    defaults[o.siteId] = (o.otherRegions && o.otherRegions[0])
                        ? o.otherRegions[0].regionId
                        : '';
                });
                console.log('[StructureManagement] PRIMARY_REGION_ORPHAN 409 received', body.orphans);
                setOrphanSites(body.orphans);
                setOrphanChoices(defaults);
                // Close the assign-sites modal first so the orphan modal isn't
                // stacked behind it. selectedSiteIds is preserved so the retry
                // sends the same payload with the admin's primary replacements.
                setShowAssignSitesModal(false);
                setShowOrphanModal(true);
                setIsLoading(false);
                return;
            }
            console.error("Failed to assign sites:", err);
            setError(err.response?.data?.message || "Failed to assign sites.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAssignSites = () => submitAssignSites();

    // Called from the orphan modal "Save with replacements" button.
    const handleConfirmOrphanReplacements = () => {
        // Build the replacementPrimaries map for sites whose admin chose a replacement.
        // Sites mapped to '' get cleared via allowClearPrimary.
        const replacementPrimaries = {};
        let anyCleared = false;
        Object.entries(orphanChoices).forEach(([siteId, regionChoice]) => {
            if (regionChoice === '' || regionChoice === null || regionChoice === undefined) {
                anyCleared = true;
            } else {
                replacementPrimaries[siteId] = parseInt(regionChoice, 10);
            }
        });
        submitAssignSites({
            replacementPrimaries,
            allowClearPrimary: anyCleared
        });
    };
    // Filter sites based on search
    const filteredSites = availableSites.filter(site =>
        site.name.toLowerCase().includes(siteSearchTerm.toLowerCase()) ||
        site.address.toLowerCase().includes(siteSearchTerm.toLowerCase())
    );
    const openAssignPersonnelModal = async (entity, type) => {
        setTargetEntity({ ...entity, type });
        setPersonnelSearchTerm('');
        setShowAssignPersonnelModal(true);
        setIsLoading(true);
        try {
            const response = await personnelAPI.getAll();
            const personnel = response.data;
            setAvailablePersonnel(personnel);
            // Pre-select already assigned OR inherited users
            const initialSelection = [];
            const directRegionIds = [];
            personnel.forEach(p => {
                if (type === 'branch') {
                    // Check if personnel is assigned to this branch (M:N)
                    if (p.branches && p.branches.some(b => b.id === entity.id)) {
                        initialSelection.push(p.id);
                    }
                } else if (type === 'region') {
                    // Check M:N relationship
                    if (p.regions && p.regions.some(r => r.id === entity.id)) {
                        initialSelection.push(p.id);
                        directRegionIds.push(p.id);
                    }
                    // Also select inherited (visually) - Branch staff
                    // Check if personnel is assigned to this region's branch
                    // Branch personnel should ALWAYS be inherited regardless of other region assignments
                    if (p.branches && p.branches.some(b => b.id === entity.branchId)) {
                        initialSelection.push(p.id);
                    }
                }
            });
            setSelectedPersonnelIds(initialSelection);
            setInitialRegionPersonnelIds(directRegionIds);
        } catch (err) {
            console.error("Failed to fetch personnel:", err);
            setError("Failed to load personnel. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };
    const handlePersonnelSelectionChange = (id) => {
        setSelectedPersonnelIds(prev => {
            if (prev.includes(id)) {
                return prev.filter(pId => pId !== id);
            } else {
                return [...prev, id];
            }
        });
    };
    const handleAssignPersonnel = async () => {
        if (!targetEntity) return;
        setIsLoading(true);
        setError('');
        try {
            // Filter out inherited users from the payload to avoid demoting them
            // inherited users are those who are implicitly assigned via parent
            let idsToAssign = [...selectedPersonnelIds];
            if (targetEntity.type === 'region') {
                // Branch personnel should ALWAYS be inherited regardless of other region assignments
                const inheritedIds = availablePersonnel
                    .filter(p => p.branches && p.branches.some(b => b.id === targetEntity.branchId))
                    .map(p => p.id);
                idsToAssign = idsToAssign.filter(id => !inheritedIds.includes(id));
            }
            if (targetEntity.type === 'branch') {
                await branchesAPI.assignPersonnel(targetEntity.id, idsToAssign);
            } else {
                await regionsAPI.assignPersonnel(targetEntity.id, idsToAssign);
                // Propagate adds/removes to every site under this region
                const addedToRegion = idsToAssign.filter(id => !initialRegionPersonnelIds.includes(id));
                const removedFromRegion = initialRegionPersonnelIds.filter(id => !idsToAssign.includes(id));
                if (addedToRegion.length > 0 || removedFromRegion.length > 0) {
                    const sitesResponse = await regionsAPI.getAssignedSites(targetEntity.id);
                    const rawSites = sitesResponse.data;
                    const regionSites = Array.isArray(rawSites) ? rawSites : (rawSites?.sites || rawSites?.data || []);
                    await Promise.all(regionSites.map(async (site) => {
                        try {
                            const sitePersonnelResponse = await sitesAPI.getPersonnel(site.id);
                            const currentIds = (sitePersonnelResponse.data || []).map(p => p.id);
                            const newIds = [
                                ...new Set([
                                    ...currentIds.filter(id => !removedFromRegion.includes(id)),
                                    ...addedToRegion
                                ])
                            ];
                            await sitesAPI.assignPersonnel(site.id, newIds);
                        } catch (siteErr) {
                            console.error(`Failed to update personnel for site ${site.id}:`, siteErr);
                        }
                    }));
                }
            }
            setShowAssignPersonnelModal(false);
            const count = idsToAssign.length;
            setSuccess(
                targetEntity.type === 'region'
                    ? (count > 0
                        ? `Successfully assigned ${count} personnel to ${targetEntity.name} and all its sites!`
                        : `Successfully cleared all personnel from ${targetEntity.name} and its sites!`)
                    : (count > 0
                        ? `Successfully assigned ${count} personnel to ${targetEntity.name}!`
                        : `Successfully cleared all personnel from ${targetEntity.name}!`)
            );
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error("Failed to assign personnel:", err);
            setError(err.response?.data?.message || "Failed to assign personnel.");
        } finally {
            setIsLoading(false);
        }
    };
    // Filter personnel
    const filteredPersonnel = availablePersonnel.filter(p =>
        p.name && p.name.toLowerCase().includes(personnelSearchTerm.toLowerCase()) ||
        p.email && p.email.toLowerCase().includes(personnelSearchTerm.toLowerCase())
    );
    const handleDownloadSites = async (region, format) => {
        setIsLoading(true);
        setError('');
        try {
            const apiMethod = format === 'excel' ? regionsAPI.exportSitesExcel : regionsAPI.exportSitesPdf;
            const response = await apiMethod(region.id);
            // Create blob URL and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const extension = format === 'excel' ? 'xlsx' : 'pdf';
            const fileName = `sites_${region.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${extension}`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setSuccess(`Successfully downloaded sites for ${region.name} in ${format.toUpperCase()}!`);
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error(`Failed to download sites in ${format}:`, err);
            setError(`Failed to download sites in ${format}. Please try again.`);
        } finally {
            setIsLoading(false);
        }
    };
    // Helper to determine status and locking
    const getPersonnelStatus = (person) => {
        if (targetEntity?.type === 'region') {
            // Inherited from Branch (assigned to branch)
            if (person.branches && person.branches.some(b => b.id === targetEntity.branchId)) {
                return { isInherited: true, isLocked: true, label: 'Inherited (Branch)' };
            }
        }
        return { isInherited: false, isLocked: false, label: '' };
    };

    // Export all branches, regions, and sites structure to Excel
    const handleExportStructure = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await branchesAPI.exportStructureExcel();
            
            // Create blob URL and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `Branches and Regions.xlsx`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            setSuccess('Successfully exported structure to Excel!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Failed to export structure:', err);
            setError('Failed to export structure to Excel. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Open delete confirmation modal
    const openDeleteModal = (type, entity) => {
        setDeleteTarget({ type, id: entity.id, name: entity.name });
        setShowDeleteModal(true);
    };

    // Handle delete confirmation
    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        
        setIsLoading(true);
        setError('');
        try {
            if (deleteTarget.type === 'branch') {
                await branchesAPI.delete(deleteTarget.id);
                notificationService.triggerStructureAction('branch-deleted', deleteTarget.name);
                setSuccess(`Branch "${deleteTarget.name}" deleted successfully!`);
            } else if (deleteTarget.type === 'region') {
                await regionsAPI.delete(deleteTarget.id);
                notificationService.triggerStructureAction('region-deleted', deleteTarget.name);
                setSuccess(`Region "${deleteTarget.name}" deleted successfully!`);
            }
            
            setShowDeleteModal(false);
            setDeleteTarget(null);
            fetchBranches(); // Refresh the list
            fetchAllSites(); // Refresh sites to update counts
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error(`Failed to delete ${deleteTarget.type}:`, err);
            setError(err.response?.data?.message || `Failed to delete ${deleteTarget.type}. Please try again.`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container fluid>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="mb-0">Structure Management</h1>
                <Button variant="primary" onClick={() => setShowAddBranchModal(true)}>
                    <FaPlus className="me-2" />
                    Add Branch
                </Button>
            </div>
            {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <Card.Title className="mb-0">Branches & Regions</Card.Title>
                        <Button
                            variant="success"
                            size="sm"
                            onClick={handleExportStructure}
                            disabled={isLoading || branches.length === 0}
                            title="Export all branches, regions, and sites to Excel"
                        >
                            <FaFileExcel className="me-2" />
                            Export to Excel
                        </Button>
                    </div>
                    <Table responsive hover>
                        <thead>
                            <tr>
                                <th style={{ width: '50%' }}>Name</th>
                                <th style={{ width: '15%' }}>Type</th>
                                <th style={{ width: '35%' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {branches.length > 0 ? (
                                branches.map(branch => (
                                    <React.Fragment key={branch.id}>
                                        {/* Branch Row */}
                                        <tr>
                                            <td colSpan="2">
                                                <strong>{branch.name}</strong>
                                                <Badge bg="secondary" className="ms-2">
                                                    {branch.regions ? branch.regions.length : 0} Regions
                                                </Badge>
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => openAddRegionModal(branch)}
                                                    title="Add Region"
                                                >
                                                    <FaMapMarkedAlt className="me-1" /> Add Region
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => openAssignPersonnelModal(branch, 'branch')}
                                                    title="Assign Personnel to Branch"
                                                >
                                                    <FaUserPlus className="me-1" /> Assign Personnel
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => openDeleteModal('branch', branch)}
                                                    title="Delete Branch"
                                                >
                                                    <FaTrash className="me-1" /> Delete
                                                </Button>
                                            </td>
                                        </tr>
                                        {/* Region Rows */}
                                        {branch.regions && branch.regions.length > 0 ? (
                                            branch.regions.map(region => {
                                                // Backend returns BOTH the M:N association (`region.sites`)
                                                // and the legacy hasMany (`region.regionSites`). Merge them
                                                // by id so multi-assigned sites and legacy-only sites both
                                                // count once.
                                                const mergedSitesById = new Map();
                                                (region.sites || []).forEach(s => mergedSitesById.set(s.id, s));
                                                (region.regionSites || []).forEach(s => {
                                                    if (!mergedSitesById.has(s.id)) mergedSitesById.set(s.id, s);
                                                });
                                                const regionSiteList = Array.from(mergedSitesById.values());
                                                return (
                                                <tr key={region.id}>
                                                    <td style={{ paddingLeft: '40px' }} className="text-muted">
                                                        <FaMapMarkerAlt className="me-2 is-size-7" />
                                                        {region.name}
                                                        <OverlayTrigger
                                                            trigger={['hover', 'focus', 'click']}
                                                            placement="right"
                                                            overlay={
                                                                <Popover id={`popover-sites-${region.id}`}>
                                                                    <Popover.Header as="h3" style={{ fontSize: '0.9rem' }}>Sites in {region.name}</Popover.Header>
                                                                    <Popover.Body>
                                                                        {regionSiteList.length > 0 ? (
                                                                            <ul className="mb-0 ps-3" style={{ fontSize: '0.85rem' }}>
                                                                                {regionSiteList.map(site => {
                                                                                    // M:N attached `primaryRegionId` says where this
                                                                                    // site's primary actually lives. If it matches
                                                                                    // THIS region, the site is primary here; otherwise
                                                                                    // it's a secondary membership. Legacy hasMany rows
                                                                                    // (no primaryRegionId field) are always primary by
                                                                                    // definition (regionId === this region).
                                                                                    const isPrimaryHere = site.primaryRegionId === undefined
                                                                                        ? true
                                                                                        : site.primaryRegionId === region.id;
                                                                                    return (
                                                                                        <li key={site.id}>
                                                                                            {site.name}{' '}
                                                                                            <span className={isPrimaryHere ? 'text-warning' : 'text-info'} style={{ fontSize: '0.75rem' }}>
                                                                                                {isPrimaryHere ? '(primary)' : '(secondary)'}
                                                                                            </span>
                                                                                        </li>
                                                                                    );
                                                                                })}
                                                                            </ul>
                                                                        ) : (
                                                                            <div className="text-muted small">No sites assigned</div>
                                                                        )}
                                                                    </Popover.Body>
                                                                </Popover>
                                                            }
                                                        >
                                                            <Badge bg="light" text="dark" className="ms-2 border" style={{ cursor: 'pointer' }}>
                                                                {regionSiteList.length} Sites
                                                            </Badge>
                                                        </OverlayTrigger>
                                                    </td>
                                                    <td>
                                                        <Badge bg="info" className="text-dark">Region</Badge>
                                                    </td>
                                                    <td>
                                                        {/* Future: Edit/Delete Region actions */}
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="p-0 text-decoration-none"
                                                            onClick={() => openAssignSitesModal(region)}
                                                            title="Add Sites to Region"
                                                        >
                                                            <FaPlus className="me-1" /> Add Sites
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="p-0 text-decoration-none ms-3"
                                                            onClick={() => openAssignPersonnelModal(region, 'region')}
                                                            title="Assign Personnel to Region"
                                                        >
                                                            <FaUserPlus className="me-1" /> Assign Staff
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="p-0 text-decoration-none ms-3 text-success"
                                                            onClick={() => handleDownloadSites(region, 'excel')}
                                                            title="Download Sites (Excel)"
                                                            disabled={isLoading}
                                                        >
                                                            <FaFileExcel className="me-1" /> Excel
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="p-0 text-decoration-none ms-3 text-danger"
                                                            onClick={() => handleDownloadSites(region, 'pdf')}
                                                            title="Download Sites (PDF)"
                                                            disabled={isLoading}
                                                        >
                                                            <FaFilePdf className="me-1" /> PDF
                                                        </Button>
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="p-0 text-decoration-none ms-3 text-danger"
                                                            onClick={() => openDeleteModal('region', region)}
                                                            title="Delete Region"
                                                        >
                                                            <FaTrash className="me-1" /> Delete
                                                        </Button>
                                                    </td>
                                                </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="text-muted small fst-italic ps-5">
                                                    No regions added yet
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="text-center">No branches found</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
            {/* Add Branch Modal */}
            <Modal show={showAddBranchModal} onHide={() => setShowAddBranchModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Branch</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                    <Form onSubmit={handleAddBranch}>
                        <Form.Group className="mb-3">
                            <Form.Label>Branch Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={branchFormData.name}
                                onChange={handleBranchInputChange}
                                required
                                placeholder="e.g., Nairobi Region"
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" className="me-2" onClick={() => setShowAddBranchModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isLoading}>
                                {isLoading ? 'Adding...' : 'Add Branch'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            {/* Add Region Modal */}
            <Modal show={showAddRegionModal} onHide={() => setShowAddRegionModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Region to {selectedBranch?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                    <Form onSubmit={handleAddRegion}>
                        <Form.Group className="mb-3">
                            <Form.Label>Region Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={regionFormData.name}
                                onChange={handleRegionInputChange}
                                required
                                placeholder="e.g., Westlands"
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" className="me-2" onClick={() => setShowAddRegionModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isLoading}>
                                {isLoading ? 'Adding...' : 'Add Region'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
            {/* Bulk Assign Sites Modal */}
            <Modal show={showAssignSitesModal} onHide={() => setShowAssignSitesModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Add Sites to {selectedRegion?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Search sites by name or address..."
                            value={siteSearchTerm}
                            onChange={(e) => setSiteSearchTerm(e.target.value)}
                        />
                    </Form.Group>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {isLoading && !availableSites.length ? (
                            <div className="text-center p-3">Loading sites...</div>
                        ) : (
                            <Table hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>Select</th>
                                        <th>Name</th>
                                        <th>Address</th>
                                        <th>Current Region</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSites.map(site => {
                                        // Build a deduped list of every region the site belongs to.
                                        // `regionDetails` is the primary; `regions[]` is the M:N membership
                                        // from SiteRegions (includes the primary AND every secondary).
                                        const regionList = [];
                                        const seen = new Set();
                                        if (site.regionDetails) {
                                            regionList.push(site.regionDetails);
                                            seen.add(site.regionDetails.id);
                                        }
                                        if (Array.isArray(site.regions)) {
                                            site.regions.forEach(r => {
                                                if (!seen.has(r.id)) {
                                                    regionList.push(r);
                                                    seen.add(r.id);
                                                }
                                            });
                                        }
                                        const hasAnyRegion = regionList.length > 0 || !!site.regionId;

                                        // Relationship between THIS site and the region we're editing:
                                        const editingRegionId = selectedRegion?.id;
                                        const isPrimaryHere = !!editingRegionId && site.regionId === editingRegionId;
                                        const isSecondaryHere = !!editingRegionId
                                            && !isPrimaryHere
                                            && Array.isArray(site.regions)
                                            && site.regions.some(r => r.id === editingRegionId);
                                        const isAssignedElsewhereAsPrimary = !!site.regionId && site.regionId !== editingRegionId;

                                        // Build the "Current Region" cell with a 'primary' marker.
                                        const currentRegionLabel = regionList.length > 0
                                            ? regionList.map(r => {
                                                const isThisOnePrimary = site.regionId === r.id;
                                                return `${r.name}${isThisOnePrimary ? ' (primary)' : ''}`;
                                            }).join(', ')
                                            : (site.branchDetails ? site.branchDetails.name : '-');

                                        return (
                                            <tr key={site.id} className={selectedSiteIds.includes(site.id) ? 'table-primary' : ''}>
                                                <td>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selectedSiteIds.includes(site.id)}
                                                        onChange={() => handleSiteSelectionChange(site.id)}
                                                        title={hasAnyRegion ? `Currently assigned to: ${currentRegionLabel}` : "Select site"}
                                                    />
                                                </td>
                                                <td>
                                                    {site.name}
                                                    {isPrimaryHere && (
                                                        <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7em' }}>
                                                            Primary here
                                                        </Badge>
                                                    )}
                                                    {isSecondaryHere && (
                                                        <Badge bg="info" className="ms-2" style={{ fontSize: '0.7em' }}>
                                                            Secondary here
                                                        </Badge>
                                                    )}
                                                    {!isPrimaryHere && !isSecondaryHere && isAssignedElsewhereAsPrimary && (
                                                        <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7em' }}>
                                                            Primary elsewhere
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td>{site.address}</td>
                                                <td className="text-muted small">
                                                    {currentRegionLabel}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {filteredSites.length === 0 && (
                                        <tr><td colSpan="4" className="text-center text-muted">No sites found</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                        <div className="text-muted">
                            {selectedSiteIds.length} sites selected
                        </div>
                        <div>
                            <Button variant="secondary" className="me-2" onClick={() => setShowAssignSitesModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleAssignSites}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Assigning...' : 'Add Selected Sites'}
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >
            {/* Primary-region orphan modal — shown when removing a site from its
                primary region. Admin picks a replacement primary region (or
                accepts clearing) before the assign-sites save proceeds. */}
            <Modal show={showOrphanModal} onHide={() => setShowOrphanModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Pick replacement primary regions</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">
                        The site{orphanSites.length === 1 ? '' : 's'} below
                        {orphanSites.length === 1 ? ' has' : ' have'} <strong>{selectedRegion?.name}</strong> set as
                        the primary region, and {orphanSites.length === 1 ? "it's" : "they're"} being removed from it.
                        Choose which of {orphanSites.length === 1 ? 'its' : 'their'} other regions should become the
                        new primary, or pick "Clear primary" to leave it without one.
                    </p>
                    <Table size="sm" responsive hover>
                        <thead>
                            <tr>
                                <th>Site</th>
                                <th>New primary region</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orphanSites.map(o => (
                                <tr key={o.siteId}>
                                    <td>{o.siteName}</td>
                                    <td>
                                        <Form.Select
                                            size="sm"
                                            value={orphanChoices[o.siteId] ?? ''}
                                            onChange={(e) => setOrphanChoices(prev => ({
                                                ...prev,
                                                [o.siteId]: e.target.value
                                            }))}
                                        >
                                            <option value="">Clear primary (no replacement)</option>
                                            {o.otherRegions.map(r => (
                                                <option key={r.regionId} value={r.regionId}>
                                                    {r.regionName}{r.branchName ? ` (${r.branchName})` : ''}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowOrphanModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirmOrphanReplacements} disabled={isLoading}>
                        {isLoading ? 'Saving...' : 'Save with replacements'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Bulk Assign Personnel Modal */}
            <Modal show={showAssignPersonnelModal} onHide={() => setShowAssignPersonnelModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Assign Personnel to {targetEntity?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Search personnel by name or email..."
                            value={personnelSearchTerm}
                            onChange={(e) => setPersonnelSearchTerm(e.target.value)}
                        />
                    </Form.Group>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {isLoading && !availablePersonnel.length ? (
                            <div className="text-center p-3">Loading personnel...</div>
                        ) : (
                            <Table hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>Select</th>
                                        <th>Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPersonnel.map(person => {
                                        const status = getPersonnelStatus(person);
                                        const isSelected = selectedPersonnelIds.includes(person.id);
                                        return (
                                            <tr key={person.id} className={isSelected ? 'table-primary' : ''}>
                                                <td>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        disabled={status.isLocked}
                                                        onChange={() => handlePersonnelSelectionChange(person.id)}
                                                    />
                                                </td>
                                                <td>
                                                    {person.name}
                                                    {status.isInherited && (
                                                        <Badge bg="info" className="ms-2">Inherited</Badge>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {filteredPersonnel.length === 0 && (
                                        <tr><td colSpan="2" className="text-center text-muted">No personnel found</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                        <div className="text-muted">
                            {selectedPersonnelIds.length} personnel selected
                        </div>
                        <div>
                            <Button variant="secondary" className="me-2" onClick={() => setShowAssignPersonnelModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleAssignPersonnel}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Assigning...' : 'Assign Selected'}
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal >

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete the {deleteTarget?.type} <strong>"{deleteTarget?.name}"</strong>?</p>
                    {deleteTarget?.type === 'branch' && (
                        <div 
                            style={{
                                backgroundColor: '#FEF2F2',
                                border: '1px solid #DC2626',
                                borderRadius: '0.375rem',
                                padding: '0.75rem 1rem',
                                marginBottom: '1rem'
                            }}
                        >
                            <strong style={{ color: '#DC2626' }}>Warning:</strong>{' '}
                            <span style={{ color: '#1F2937' }}>
                                Deleting this branch will also remove all associated regions and their relationships.
                            </span>
                        </div>
                    )}
                    {deleteTarget?.type === 'region' && (
                        <div 
                            style={{
                                backgroundColor: '#FEF2F2',
                                border: '1px solid #DC2626',
                                borderRadius: '0.375rem',
                                padding: '0.75rem 1rem',
                                marginBottom: '1rem'
                            }}
                        >
                            <strong style={{ color: '#DC2626' }}>Warning:</strong>{' '}
                            <span style={{ color: '#1F2937' }}>
                                Deleting this region will remove all site associations within this region.
                            </span>
                        </div>
                    )}
                    <p className="text-muted">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="secondary" 
                        onClick={() => setShowDeleteModal(false)}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleConfirmDelete}
                        disabled={isLoading}
                    >
                        {isLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container >
    );
};
export default StructureManagement;
